import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { mapCustomerListItemFromApi, getCustomerSelectLabel } from "../utils/formatters";
import { deleteCustomerAccount, fetchCustomerAccountsByCustomerId } from "./customerAccountService";
import { SupportSortableHeader } from "./SupportSortableHeader";
import "../assets/css/DigixLeads.css";
import "../assets/css/AccountsReceivable.css";

const ROWS_PER_PAGE = 5;

function AccountsReceivable() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
    type: "all",
  });
  const [columnWidths, setColumnWidths] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const resizeRef = useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const customerDropdownRef = useRef(null);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [freeSearch, setFreeSearch] = useState("");

  const sessionKeyForCustomerAccounts = (customerId) => `cloudOffice_finance_customerAccounts_${customerId}`;

  const readCachedCustomerAccounts = (customerId) => {
    try {
      const raw = sessionStorage.getItem(sessionKeyForCustomerAccounts(customerId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const writeCachedCustomerAccounts = (customerId, rows) => {
    try {
      if (!Array.isArray(rows)) return;
      sessionStorage.setItem(sessionKeyForCustomerAccounts(customerId), JSON.stringify(rows));
    } catch {
      // ignore quota/serialization errors
    }
  };

  const dedupeAccounts = (list) => {
    const arr = Array.isArray(list) ? list : [];
    const seen = new Set();
    const out = [];
    for (let i = 0; i < arr.length; i += 1) {
      const row = arr[i];
      const rid = row?.Customer_Account_Id ?? row?.customer_account_id ?? row?.id ?? `row-${i}`;
      const key = String(rid);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
    return out;
  };

  const fetchCustomerAccountsWithCache = async (customerId) => {
    const cid = String(customerId ?? "").trim();
    if (!cid) return [];
    const cached = readCachedCustomerAccounts(cid);
    if (cached) return cached;
    const rows = await fetchCustomerAccountsByCustomerId(cid);
    writeCachedCustomerAccounts(cid, rows);
    return rows;
  };

  /**
   * "All Clients" can be expensive (one API call per customer). To keep the page responsive:
   * - use session cache per customer
   * - fetch missing customers with a small concurrency limit
   * - progressively append rows as they arrive (no long blank loading)
   */
  const loadAllCustomerAccountsProgressive = async (list, { cancelledRef, concurrency = 6 } = {}) => {
    const ids = (list || [])
      .map((c) => c?.id)
      .filter((id) => id != null && String(id).trim() !== "")
      .map((id) => String(id));
    if (ids.length === 0) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    const initial = [];
    const queue = [];
    for (const cid of ids) {
      const cached = readCachedCustomerAccounts(cid);
      if (cached && cached.length) initial.push(...cached);
      else queue.push(cid);
    }

    // Show cached rows immediately.
    setAccounts(dedupeAccounts(initial));
    setLoading(queue.length > 0);
    if (queue.length === 0) return;

    let inFlight = 0;
    let index = 0;

    return await new Promise((resolve) => {
      const pump = () => {
        if (cancelledRef?.current) return resolve();
        if (index >= queue.length && inFlight === 0) return resolve();
        while (!cancelledRef?.current && inFlight < concurrency && index < queue.length) {
          const cid = queue[index];
          index += 1;
          inFlight += 1;
          fetchCustomerAccountsByCustomerId(cid)
            .then((rows) => {
              if (cancelledRef?.current) return;
              const safeRows = Array.isArray(rows) ? rows : [];
              writeCachedCustomerAccounts(cid, safeRows);
              if (safeRows.length) {
                setAccounts((prev) => dedupeAccounts([...(prev || []), ...safeRows]));
              }
            })
            .catch(() => {
              if (cancelledRef?.current) return;
              writeCachedCustomerAccounts(cid, []);
            })
            .finally(() => {
              inFlight -= 1;
              pump();
            });
        }
      };
      pump();
    }).finally(() => {
      if (!cancelledRef?.current) setLoading(false);
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCustomers(true);
      try {
        const response = await axiosInstance.get("/clients");
        const raw = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        const mapped = raw.map(mapCustomerListItemFromApi).filter(Boolean);
        if (!cancelled) setCustomers(mapped);
      } catch {
        if (!cancelled) setCustomers([]);
      } finally {
        if (!cancelled) setLoadingCustomers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const customerNameById = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(String(c.id), getCustomerSelectLabel(c)));
    return map;
  }, [customers]);

  const selectedCustomerLabel = selectedCustomerId
    ? customerNameById.get(String(selectedCustomerId)) || "All Clients"
    : "All Clients";

  const filteredCustomerOptions = useMemo(() => {
    const q = customerSearchQuery.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => getCustomerSelectLabel(c).toLowerCase().includes(q));
  }, [customers, customerSearchQuery]);

  useEffect(() => {
    const cancelledRef = { current: false };
    (async () => {
      if (loadingCustomers) return;
      try {
        if (selectedCustomerId) {
          setLoading(true);
          const list = await fetchCustomerAccountsWithCache(selectedCustomerId);
          if (!cancelledRef.current) setAccounts(Array.isArray(list) ? list : []);
          return;
        }

        // All Clients: progressive load with caching
        await loadAllCustomerAccountsProgressive(customers, { cancelledRef, concurrency: 6 });
      } catch {
        if (!cancelledRef.current) setAccounts([]);
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [selectedCustomerId, customers, loadingCustomers]);

  const filteredAccounts = useMemo(() => {
    const q = filters.searchText.trim().toLowerCase();
    return accounts.filter((row) => {
      const holder = String(row.Account_Holder_Name ?? row.account_holder_name ?? "").toLowerCase();
      const number = String(row.Account_Number ?? row.account_number ?? "").toLowerCase();
      const bank = String(row.Bank_Name ?? row.bank_name ?? "").toLowerCase();
      const ifsc = String(row.IFSC_Code ?? row.ifsc_code ?? "").toLowerCase();
      const upi = String(row.UPI_Id ?? row.upi_id ?? "").toLowerCase();
      const type = String(row.Account_Type ?? row.account_type ?? "").toLowerCase();
      const status = String(row.Status ?? row.status ?? "").toLowerCase();
      const cid = row.client_id ?? row.client_id;
      const customer = String(customerNameById.get(String(cid)) || cid || "").toLowerCase();
      const matchesText =
        !q || holder.includes(q) || number.includes(q) || bank.includes(q) || ifsc.includes(q) || upi.includes(q) || customer.includes(q);
      const matchesStatus = filters.status === "all" || status === filters.status;
      const matchesType = filters.type === "all" || type === filters.type;
      return matchesText && matchesStatus && matchesType;
    });
  }, [accounts, filters, customerNameById]);

  const freeSearchFiltered = useMemo(() => {
    const q = freeSearch.trim().toLowerCase();
    if (!q) return filteredAccounts;
    return filteredAccounts.filter((row) => {
      const cid = row.client_id ?? row.client_id;
      const customer = String(customerNameById.get(String(cid)) || cid || "—");
      const primary =
        row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1 ? "Yes" : "No";
      const haystack = [
        row.Account_Holder_Name ?? row.account_holder_name,
        row.Account_Number ?? row.account_number,
        row.Bank_Name ?? row.bank_name,
        row.IFSC_Code ?? row.ifsc_code,
        row.UPI_Id ?? row.upi_id,
        row.Account_Type ?? row.account_type,
        row.Status ?? row.status,
        row.Notes ?? row.notes,
        customer,
        primary,
        String(row.Customer_Account_Id ?? row.customer_account_id ?? ""),
        String(row.client_id ?? row.client_id ?? "")
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [filteredAccounts, freeSearch, customerNameById]);

  const sortedAccounts = useMemo(() => {
    if (!sortColumn) return freeSearchFiltered;
    const val = (row, key) => {
      const cid = row.client_id ?? row.client_id;
      switch (key) {
        case "holder":
          return String(row.Account_Holder_Name ?? row.account_holder_name ?? "").toLowerCase();
        case "customer":
          return String(customerNameById.get(String(cid)) || cid || "").toLowerCase();
        case "number":
          return String(row.Account_Number ?? row.account_number ?? "").toLowerCase();
        case "type":
          return String(row.Account_Type ?? row.account_type ?? "").toLowerCase();
        case "bank":
          return String(row.Bank_Name ?? row.bank_name ?? "").toLowerCase();
        case "ifsc":
          return String(row.IFSC_Code ?? row.ifsc_code ?? "").toLowerCase();
        case "upi":
          return String(row.UPI_Id ?? row.upi_id ?? "").toLowerCase();
        case "status":
          return String(row.Status ?? row.status ?? "").toLowerCase();
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = val(a, sortColumn);
      const vb = val(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.Customer_Account_Id ?? a.customer_account_id ?? 0);
      const idb = Number(b.Customer_Account_Id ?? b.customer_account_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFiltered, sortColumn, sortDirection, customerNameById]);

  const totalRows = sortedAccounts.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE));
  const showPagination = !loading && totalRows > ROWS_PER_PAGE;
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const pagedAccounts = sortedAccounts.slice(startIndex, startIndex + ROWS_PER_PAGE);
  const startRow = totalRows === 0 ? 0 : startIndex + 1;
  const endRow = Math.min(startIndex + ROWS_PER_PAGE, totalRows);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCustomerId, filters, freeSearch]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!customerDropdownRef.current) return;
      if (!customerDropdownRef.current.contains(e.target)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) return `${columnWidths[columnIndex]}px`;
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) return `${columnWidths[columnIndex]}px`;
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    resizeRef.current = {
      startX: e.clientX,
      startWidth: th.offsetWidth,
      columnIndex,
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(90, resizeRef.current.startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleDelete = async (accountId) => {
    if (accountId == null || accountId === "") return;
    const ok = window.confirm("Delete this customer account?");
    if (!ok) return;
    const key = String(accountId);
    setDeletingId(key);
    try {
      await deleteCustomerAccount(accountId);
      setAccounts((prev) =>
        prev.filter(
          (row, idx) => String(row?.Customer_Account_Id ?? row?.customer_account_id ?? `row-${idx}`) !== key
        )
      );
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Failed to delete customer account.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="accounts-payable">
      <h2 className="accounts-receivable-section-title">Customer accounts</h2>
      <div className="search-actions-row accounts-payable-search-actions-row">
        <div className="search-filters-row accounts-payable-toolbar-right">
          <input
            className="search-filter-input"
            placeholder="Search customer, account number..."
            value={filters.searchText}
            onChange={(e) => setFilters((prev) => ({ ...prev, searchText: e.target.value }))}
          />
          <div className="accounts-filter-dropdown" ref={customerDropdownRef}>
            <div
              className="search-filter-input search-filter-select accounts-filter-trigger"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!loadingCustomers) setCustomerDropdownOpen((o) => !o);
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !loadingCustomers) {
                  e.preventDefault();
                  setCustomerDropdownOpen((o) => !o);
                }
              }}
              aria-expanded={customerDropdownOpen}
            >
              <span>{loadingCustomers ? "Loading..." : selectedCustomerLabel}</span>
              <FaChevronDown className={`accounts-filter-chevron ${customerDropdownOpen ? "open" : ""}`} />
            </div>
            {customerDropdownOpen && !loadingCustomers ? (
              <div className="accounts-filter-panel">
                <input
                  type="text"
                  className="accounts-filter-search"
                  placeholder="Search customers..."
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="accounts-filter-options">
                  <div
                    className={`accounts-filter-option ${!selectedCustomerId ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedCustomerId("");
                      setCustomerDropdownOpen(false);
                      setCustomerSearchQuery("");
                      setCurrentPage(1);
                    }}
                  >
                    All Clients
                  </div>
                  {filteredCustomerOptions.length > 0 ? (
                    filteredCustomerOptions.map((c) => (
                      <div
                        key={c.id}
                        className={`accounts-filter-option ${String(selectedCustomerId) === String(c.id) ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedCustomerId(String(c.id));
                          setCustomerDropdownOpen(false);
                          setCustomerSearchQuery("");
                          setCurrentPage(1);
                        }}
                      >
                        {getCustomerSelectLabel(c)}
                      </div>
                    ))
                  ) : (
                    <div className="accounts-filter-no-results">No customers found</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <select
            className="search-filter-input search-filter-select"
            value={filters.type}
            onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="all">All Types</option>
            <option value="savings">Savings</option>
            <option value="current">Current</option>
          </select>
          <select
            className="search-filter-input search-filter-select"
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              const reset = { searchText: "", status: "all", type: "all" };
              setFilters(reset);
              setFreeSearch("");
            }}
          >
            Clear
          </button>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-primary btn-add-lead btn-new-customer-account"
            onClick={() => navigate("/finance-management/payable/customer-account/new")}
          >
            New Customer Account
          </button>
        </div>
      </div>

      <div className="search-row-2">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={freeSearch}
            onChange={(e) => setFreeSearch(e.target.value)}
            title="Search across all columns"
          />
        </div>
      </div>

      <div className="accounts-payable-table-wrap">
        <table className="accounts-payable-table accounts-payable-table-resizable">
          <thead>
            <tr>
              <th
                className="accounts-payable-sticky-first accounts-payable-th-sortable"
                style={{ minWidth: getColumnMinWidth(0) || "160px", width: getColumnWidth(0), position: "relative" }}
              >
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="holder" label="Account Holder" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 0)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(1) || "170px", width: getColumnWidth(1), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="customer" label="Client" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 1)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(2) || "130px", width: getColumnWidth(2), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="number" label="Account Number" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 2)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(3) || "110px", width: getColumnWidth(3), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="type" label="Type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 3)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="bank" label="Bank" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 4)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(5) || "110px", width: getColumnWidth(5), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="ifsc" label="IFSC" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 5)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(6) || "140px", width: getColumnWidth(6), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="upi" label="UPI ID" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 6)}></span>
              </th>
              <th className="accounts-payable-th-sortable" style={{ minWidth: getColumnMinWidth(7) || "100px", width: getColumnWidth(7), position: "relative" }}>
                <div className="accounts-payable-th-sort-row">
                  <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 7)}></span>
              </th>
              <th
                className="accounts-payable-sticky-last"
                style={{ minWidth: getColumnMinWidth(8) || "110px", width: getColumnWidth(8), position: "relative" }}
              >
                <div>Actions</div>
                <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 8)}></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && accounts.length === 0 ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">
                  Loading customer accounts...
                </td>
              </tr>
            ) : filteredAccounts.length === 0 ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">
                  No accounts found for selected filters.
                </td>
              </tr>
            ) : freeSearchFiltered.length === 0 ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">
                  No accounts match your search.
                </td>
              </tr>
            ) : (
              pagedAccounts.map((row, idx) => {
                const id = row.Customer_Account_Id ?? row.customer_account_id ?? `row-${idx}`;
                const cid = row.client_id ?? row.client_id;
                return (
                  <tr key={String(id)}>
                    <td className="accounts-payable-sticky-first" style={{ minWidth: getColumnMinWidth(0) || "160px", width: getColumnWidth(0) }}>
                      {id ? (
                        <span
                          className="table-name-link"
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/finance-management/payable/customer-account/${id}/view`)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/finance-management/payable/customer-account/${id}/view`);
                            }
                          }}
                        >
                          {row.Account_Holder_Name ?? row.account_holder_name ?? "—"}
                        </span>
                      ) : (
                        row.Account_Holder_Name ?? row.account_holder_name ?? "—"
                      )}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(1) || "170px", width: getColumnWidth(1) }}>
                      {customerNameById.get(String(cid)) || cid || "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(2) || "130px", width: getColumnWidth(2) }}>
                      {row.Account_Number ?? row.account_number ?? "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(3) || "110px", width: getColumnWidth(3) }}>
                      {row.Account_Type ?? row.account_type ?? "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4) }}>
                      {row.Bank_Name ?? row.bank_name ?? "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(5) || "110px", width: getColumnWidth(5) }}>
                      {row.IFSC_Code ?? row.ifsc_code ?? "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(6) || "140px", width: getColumnWidth(6) }}>
                      {row.UPI_Id ?? row.upi_id ?? "—"}
                    </td>
                    <td style={{ minWidth: getColumnMinWidth(7) || "100px", width: getColumnWidth(7) }}>
                      {row.Status ?? row.status ?? "—"}
                    </td>
                    <td
                      className="accounts-payable-actions-cell accounts-payable-sticky-last"
                      style={{ minWidth: getColumnMinWidth(8) || "110px", width: getColumnWidth(8) }}
                    >
                      {id ? (
                        <>
                          <button
                            type="button"
                            className="accounts-payable-action-btn"
                            title="Edit account"
                            aria-label="Edit account"
                            onClick={() => navigate(`/finance-management/payable/customer-account/${id}/edit`)}
                            disabled={deletingId === String(id)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="accounts-payable-action-btn accounts-payable-action-btn-delete"
                            title="Delete account"
                            aria-label="Delete account"
                            onClick={() => handleDelete(id)}
                            disabled={deletingId === String(id)}
                          >
                            <FaTrash />
                          </button>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="pagination">
          <button
            className="pagination-btn pagination-arrow"
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FaChevronLeft />
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} className="page-number ellipsis">
                  ...
                </span>
              ) : (
                <span
                  key={page}
                  className={`page-number ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </span>
              )
            )}
          </div>
          <button
            className="pagination-btn pagination-arrow"
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <FaChevronRight />
          </button>
          {totalRows > 0 && (
            <div className="pagination-info">
              {startRow}-{endRow} of {totalRows} items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountsReceivable;
